import { useState, useRef, FC, ChangeEvent } from 'react';
import TextField from '@material-ui/core/TextField';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { Alert, Backdrop, CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { withStyles } from '@mui/styles';
import { postBackstage } from 'APIs/BackstageApi';
import { useAppSelector } from 'app/hooks';
import ColorButton from 'components/atoms/Button/ColorButton';
import StatusButton from 'components/atoms/Button/StatusButton';
import MultiMediaDisplayPreview from 'components/molecules/MultiMediaDisplayPreview';
import { selectAuth } from 'features/auth/authSlice';
import Backstage, {
  IMediaFile,
  PostType,
} from 'interfaces/Backstage.interface';
import { uploadFileUtil } from 'utils/Fileupload.utils';
import MultiMediaEdit from 'components/molecules/MultiMediaEdit';
import LockContentModal from 'components/molecules/LockContentModal';
import InviteCreators from '../InviteCreators';
import { UserData } from 'interfaces/UserData.interface';
import { handleInviteCreatorForPost } from 'APIs/utils';

const WhiteTextField = withStyles({
  root: {
    '& .MuiInputBase-root': {
      color: '#fff',
    },
    '& .MuiInputBase-root::before': {
      borderColor: '#fff',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderColor: '#fff',
    },
    width: '100%',
  },
})(TextField);

const ExpandMore = styled((props: any) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(0deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const TitleSectionContent = styled(CardContent)(({ theme }) => ({
  padding: '0rem 16px',
}));

const ContentText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#ffffff',
  fontWeight: 'bold',
}));

const HeadSectionContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '11px',
}));

const PictureButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '5px',
  cursor: 'pointer',
  userSelect: 'none',
}));

const FooterSectionContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 'unset',
  paddingTop: '20px',
  paddingLeft: '16px',
  paddingRight: '16px',
}));

const FooterSectionContent1 = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '25px',
  padding: 'unset',
}));

const Input = styled('input')({
  display: 'none',
});

interface IProfileOwnPostCard {
  fetchData?: () => void;
  addPost?: (post: Backstage) => void;
  backstagePosts: Backstage[];
}

interface CreateBackstageDto extends Partial<Backstage> {
  mediaFiles: IMediaFile[] | [];
}

const ProfileOwnPostCard: FC<IProfileOwnPostCard> = ({
  fetchData,
  backstagePosts,
  addPost,
}) => {
  const authState = useAppSelector(selectAuth);
  const [expanded1] = useState<boolean>(false);
  const [expanded2, setExpanded2] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [editingMode, setEditingMode] = useState<boolean>(false);
  const [invitedUser, setInvitedUser] = useState<UserData>();
  const [splitPercentage, setSplitPercentage] = useState<number>(0);
  const [success, setSuccess] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);
  const [premium, setPremium] = useState({
    isPremium: false,
    price: '0.00',
  });
  const [formData, setFormData] = useState<CreateBackstageDto>({
    content: '',
    videos: [],
    images: [],
    mediaFiles: [],
    ...premium,
  });
  const pictureInputRef = useRef<HTMLInputElement | any>(null);
  const videoInputRef = useRef<HTMLInputElement | any>(null);

  const getUrlExtension = (url: string) => {
    const urlParts = url.split(/[#?]/)[0];
    if (urlParts) {
      // eslint-disable-next-line
      return urlParts?.split('.')?.pop()?.trim() || '';
    }
    return '';
  };

  const handleExpand2Click = (): void => {
    setExpanded2(!expanded2);
  };

  const handlePictureInput = (): void => {
    pictureInputRef.current.click();
  };

  const handleVideoInput = (): void => {
    videoInputRef.current.click();
  };

  const createBackstage = async (data: Partial<Backstage>) => {
    const res = await postBackstage(data);
    if (res && addPost) {
      addPost(res);
    }
  };

  const handlePremium = (price: string) => {
    if (price !== '') {
      setPremium({ isPremium: true, price });
    } else {
      setPremium({ isPremium: false, price: '' });
    }
    setFormData({ ...formData, ...premium });
  };

  const handleSubmit = async () => {
    if (!formData.content && formData.mediaFiles.length <= 0) return;
    setLoading(true);
    let updatedFormData = { ...formData };

    for (let i = 0; i < formData.mediaFiles.length; i++) {
      const mediaFileData = formData.mediaFiles[i];

      var imgExt = getUrlExtension(mediaFileData.url);

      const response = await fetch(mediaFileData.url);
      const blob = await response.blob();

      const currentTimestamp = new Date().getTime();

      const mediaFile = new File([blob], `${currentTimestamp}` + imgExt, {
        type: blob.type,
      });

      const mediaUrl = await uploadFileUtil(
        mediaFile,
        setProgress,
        false,
        false,
        undefined,
        true
      );

      if (mediaFileData.type === PostType.IMAGE) {
        const images = updatedFormData.images ? updatedFormData.images : [];
        updatedFormData = {
          ...updatedFormData,
          images: [...images, mediaUrl],
        };
      } else if (mediaFileData.type === PostType.VIDEO) {
        const videos = updatedFormData.videos ? updatedFormData.videos : [];
        updatedFormData = {
          ...updatedFormData,
          videos: [...videos, mediaUrl],
        };
      }
    }
    try {
      if (invitedUser) {
        await handleInviteCreatorForPost(
          authState,
          invitedUser,
          splitPercentage,
          updatedFormData,
          setSuccess,
          setError
        );
        setSuccess(true);
      } else {
        await createBackstage({ ...updatedFormData });
      }
      setFormData({
        content: '',
        videos: [],
        images: [],
        mediaFiles: [],
        isPremium: false,
        price: '',
      });
      setInvitedUser(undefined);
      setError(false);
    } catch (e) {
      console.log(e);
      setSuccess(false);
      setError(true);
    }
    setLoading(false);
  };

  const handleMediaChange = async (
    e: ChangeEvent<HTMLInputElement>,
    type: PostType
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const mediaFiles = [];
      for (let i = 0; i < e.target.files.length; i++) {
        const fileUrl = URL.createObjectURL(e.target.files[i]);
        const mediaFile = {
          type: type,
          url: fileUrl,
        };
        mediaFiles.push(mediaFile);
      }

      setFormData({
        ...formData,
        mediaFiles: [...formData.mediaFiles, ...mediaFiles],
      });

      setTimeout(() => {
        pictureInputRef.current.value = null;
        videoInputRef.current.value = null;
      }, 0);
    }
  };

  const handleClearMedia = () => {
    setFormData({
      ...formData,
      mediaFiles: [],
    });
  };

  const handleFilterMedia = (url: string) => {
    const formDataMediaFiles = [...formData.mediaFiles].filter(
      mediaFile => mediaFile.url !== url
    );
    setFormData(formData => ({ ...formData, mediaFiles: formDataMediaFiles }));
  };

  console.log('authState', authState);

  return (
    <>
      <Card
        sx={{
          width: '100%',
          minWidth: '300px',
          maxWidth: '850px',
          backgroundColor: 'rgba(255,255,255,0.09803921568627451 )',
          borderRadius: '20px',
          boxShadow: 'unset',
          marginTop: '40px',
        }}
      >
        {authState?.canPost && (
          <>
            <HeadSectionContent>
              <ContentText>What do you want to share?</ContentText>
              <label htmlFor="contained-button-file">
                <Input
                  accept="image/*"
                  id="contained-button-file"
                  type="file"
                  ref={pictureInputRef}
                  onChange={e => handleMediaChange(e, PostType.IMAGE)}
                  multiple
                />
                <Input
                  accept="video/*"
                  id="contained-button-file"
                  type="file"
                  ref={videoInputRef}
                  onChange={e => handleMediaChange(e, PostType.VIDEO)}
                  multiple
                />
                <ColorButton
                  text={'Post'}
                  color={'yellow'}
                  onClick={() => handleSubmit()}
                  disabled={loading}
                />
              </label>
            </HeadSectionContent>

            <TitleSectionContent>
              <WhiteTextField
                multiline
                autoFocus
                value={formData.content}
                onChange={e =>
                  setFormData({ ...formData, content: e.target.value })
                }
              />
            </TitleSectionContent>
            {formData.mediaFiles.length > 0 && editingMode ? (
              <MultiMediaEdit
                editingMode={editingMode}
                handleEditMode={() => {
                  setEditingMode(editingMode => !editingMode);
                }}
                mediaFiles={formData.mediaFiles}
                handleFilterMedia={handleFilterMedia}
              />
            ) : (
              <MultiMediaDisplayPreview
                handleClearMedia={handleClearMedia}
                mediaFiles={formData.mediaFiles}
                handleEditMode={() => {
                  setEditingMode(editingMode => !editingMode);
                }}
                editingMode={editingMode}
              />
            )}

            <FooterSectionContent>
              <FooterSectionContent1 style={{ paddingBottom: '0px' }}>
                <PictureButton onClick={() => handlePictureInput()}>
                  <CameraAltOutlinedIcon
                    style={{ fontSize: '20px', color: '#ffffff' }}
                  />
                  <Typography style={{ fontSize: '14px', color: '#ffffff' }}>
                    Picture
                  </Typography>
                </PictureButton>

                <ExpandMore
                  expand={expanded1}
                  // onClick={handleExpand1Click}
                  aria-expanded={expanded1}
                  aria-label="show more"
                  sx={{ padding: 'unset' }}
                  onClick={() => handleVideoInput()}
                >
                  <StatusButton type={'video'} text={'Video'} />
                </ExpandMore>

                <ExpandMore
                  expand={expanded2}
                  onClick={handleExpand2Click}
                  aria-expanded={expanded2}
                  aria-label="show more"
                  sx={{ padding: 'unset' }}
                >
                  <StatusButton type={'money'} text={'Tip goal'} />
                </ExpandMore>

                <IconButton sx={{ padding: 'unset' }}>
                  <InviteCreators
                    setSplitPercentage={setSplitPercentage}
                    setInvitedUser={setInvitedUser}
                  >
                    <StatusButton type={'tag'} text={'Tag Creator'} />
                  </InviteCreators>
                </IconButton>
                {authState.walletAddress && (
                  <IconButton sx={{ padding: 'unset' }}>
                    <LockContentModal
                      handlePremium={handlePremium}
                      backstagePosts={backstagePosts}
                    >
                      <StatusButton
                        type={'lock'}
                        text={
                          (formData.price !== '' &&
                            formData.price !== '0.00' &&
                            formData.price) ||
                          'PPV'
                        }
                        isGold={true}
                      />
                    </LockContentModal>
                  </IconButton>
                )}
                <IconButton
                  sx={{ display: { lg: 'block', xs: 'none' } }}
                  onClick={() => handleClearMedia()}
                >
                  <StatusButton type={'clear'} text={'Clear all'} />
                </IconButton>
                {success && (
                  <Alert
                    sx={{ width: '100%' }}
                    severity="success"
                    onClose={() => setSuccess(false)}
                  >
                    Your post has been successfully submitted to the relevant
                    creator and will be posted once they approve it.
                  </Alert>
                )}
                {error && (
                  <Alert sx={{ width: '100%' }} severity="error">
                    There was an error in uploading your post.
                  </Alert>
                )}
              </FooterSectionContent1>
            </FooterSectionContent>
          </>
        )}
      </Card>
      {loading && (
        <Backdrop
          sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <div style={{ textAlign: 'center' }}>
            <CircularProgress
              value={authState.fileUploadProgess}
              variant="determinate"
              color="inherit"
            />
            <p>Uploading... {progress}% </p>
          </div>
        </Backdrop>
      )}
    </>
  );
};

export default ProfileOwnPostCard;
